export type EmailType = 
  | 'welcome'
  | 'password_reset'
  | 'password_changed'
  | 'billing_receipt'
  | 'payment_failed'
  | 'payment_failed_grace_period'
  | 'payment_failed_reminder'
  | 'credits_expired_payment_failed'
  | 'credit_reminder'
  | 'subscription_renewal'
  | 'subscription_activated'
  | 'subscription_created'
  | 'subscription_cancelled'
  | 'subscription_reactivated'
  | 'subscription_updated'
  | 'subscription_scheduled'
  | 'credits_purchased'
  | 'plan_upgrade'
  | 'invoice_ready'
  | 'team_invite'
  | 'invoice_download'
  | 'security_notification'
  | 'reactivation'
  | 'feedback_request'
  | 'gdpr_deletion';

export interface EmailTemplate {
  subject: string;
  header: string;
  message: string;
  ctaText?: string;
  ctaUrl?: string;
  infoboxHtml?: string;
  progressbarPercent?: number;
}

export const EMAIL_TEMPLATES: Record<EmailType, (vars?: any) => EmailTemplate> = {
  welcome: () => ({
    subject: "Welcome to Email2Contact!",
    header: "Welcome to Email2Contact!",
    message: "Thanks for joining! Your account is ready. Choose a plan to start extracting contacts directly from your Outlook signatures.",
    ctaText: "Choose a Plan",
    ctaUrl: "https://email2contact.com/plans"
  }),
  
  password_reset: (vars: { resetLink: string }) => ({
    subject: "Reset your password",
    header: "Reset your password",
    message: "Click the button below to reset your password. The link is valid for 60 minutes.",
    ctaText: "Reset Password",
    ctaUrl: vars.resetLink
  }),
  
  password_changed: (vars: { date: string }) => ({
    subject: "Your password has been changed",
    header: "Password changed successfully",
    message: `Your password was changed on ${vars.date}. If this wasn't you, please contact support immediately.`,
    ctaText: "Contact Support",
    ctaUrl: "https://email2contact.com/support"
  }),
  
  billing_receipt: (vars: { plan?: string; amount?: string; date?: string; receiptUrl?: string }) => ({
    subject: "Payment Confirmed – Email2Contact",
    header: "Payment Confirmed",
    message: "Your payment has been processed successfully. Thank you for using Email2Contact!",
    ctaText: vars.receiptUrl ? "View Receipt" : "Go to Settings",
    ctaUrl: vars.receiptUrl || "https://email2contact.com/settings",
    infoboxHtml: `
      <ul style="list-style:none;padding:0;margin:0;">
        ${vars.plan ? `<li><strong>Plan:</strong> ${vars.plan}</li>` : ''}
        ${vars.amount ? `<li><strong>Amount:</strong> €${vars.amount}</li>` : ''}
        ${vars.date ? `<li><strong>Date:</strong> ${vars.date}</li>` : ''}
      </ul>
    `
  }),
  
  payment_failed: (vars?: { amount?: string; dueDate?: string; invoiceUrl?: string }) => ({
    subject: "Payment Failed – Action Required",
    header: "We couldn't process your payment",
    message: "Your last payment attempt was unsuccessful. Please update your payment details to keep your account active.",
    ctaText: vars?.invoiceUrl ? "Retry Payment" : "Update Payment Method",
    ctaUrl: vars?.invoiceUrl || "https://email2contact.com/settings",
    infoboxHtml: vars?.amount ? `
      <p style="margin:0;"><strong>Amount due:</strong> €${vars.amount}</p>
      ${vars.dueDate ? `<p style="margin:0;"><strong>Due date:</strong> ${vars.dueDate}</p>` : ''}
    ` : undefined
  }),

  payment_failed_grace_period: (vars?: { amount?: number; reason?: string; invoice_url?: string; grace_period_end?: string; days_remaining?: number }) => ({
    subject: `⚠️ Payment Failed – You Have ${vars?.days_remaining || 3} Days to Update`,
    header: "Your payment could not be processed",
    message: `We attempted to charge your payment method but the transaction failed${vars?.reason ? ` (${vars.reason})` : ''}.\n\n**You have ${vars?.days_remaining || 3} days** to update your payment details. After **${vars?.grace_period_end || 'the grace period'}**, your remaining credits will expire and you won't be able to use Email2Contact.`,
    ctaText: "Update Payment Method Now",
    ctaUrl: vars?.invoice_url || "https://email2contact.com/settings",
    infoboxHtml: `
      <div style="background: #fff3cd; border-left: 4px solid #ff9800; padding: 15px; margin: 20px 0;">
        <strong>⚠️ Important:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          ${vars?.amount ? `<li><strong>Amount due:</strong> €${vars.amount.toFixed(2)}</li>` : ''}
          <li><strong>Grace period ends:</strong> ${vars?.grace_period_end || 'in 3 days'}</li>
          <li><strong>What happens next:</strong> If not resolved, your credits will expire and your subscription will be paused.</li>
        </ul>
      </div>
    `
  }),

  payment_failed_reminder: (vars?: { days_remaining?: number; grace_period_end?: string; credits_remaining?: number }) => {
    const isUrgent = (vars?.days_remaining || 0) <= 1;
    return {
      subject: isUrgent 
        ? `🚨 URGENT: Last Day to Update Payment – Credits Expire Tomorrow`
        : `⚠️ Payment Reminder: ${vars?.days_remaining || 2} Day(s) Left – Credits Expiring Soon`,
      header: isUrgent ? "⏰ Final Warning: Update Payment Today" : "Your payment is still pending",
      message: isUrgent
        ? `**This is your final reminder.** Your payment has not been received and your grace period ends **tomorrow (${vars?.grace_period_end || 'soon'})**.\n\nIf you don't update your payment method today, your **${vars?.credits_remaining || 0} remaining credits will expire** and you will lose access to Email2Contact.`
        : `Your last payment could not be processed. Please update your payment details within the next **${vars?.days_remaining || 2} day(s)** to avoid losing your credits.\n\nYou currently have **${vars?.credits_remaining || 0} credits** remaining.`,
      ctaText: "Update Payment Details Now",
      ctaUrl: "https://email2contact.com/settings",
      infoboxHtml: `
        <div style="background: ${isUrgent ? '#f8d7da' : '#fff3cd'}; border-left: 4px solid ${isUrgent ? '#dc3545' : '#ffc107'}; padding: 15px; margin: 20px 0;">
          <strong>${isUrgent ? '🚨 FINAL WARNING:' : '⚠️ Action Required:'}</strong>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li><strong>Time remaining:</strong> ${vars?.days_remaining || 2} day(s)</li>
            <li><strong>Credits at risk:</strong> ${vars?.credits_remaining || 0}</li>
            <li><strong>Deadline:</strong> ${vars?.grace_period_end || 'Soon'}</li>
          </ul>
        </div>
      `,
    };
  },

  credits_expired_payment_failed: (vars?: { expired_credits?: number }) => ({
    subject: "❌ Credits Expired – Subscription Paused",
    header: "Your credits have expired",
    message: `Since your payment was not received within the 3-day grace period, your **${vars?.expired_credits || 0} remaining credits have been reset to 0** and your subscription has been paused.\n\nTo reactivate your subscription and continue using Email2Contact, please update your payment method.`,
    ctaText: "Reactivate Subscription",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <div style="background: #f8d7da; border-left: 4px solid #dc3545; padding: 15px; margin: 20px 0;">
        <strong>❌ What happened:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li>Your payment could not be processed</li>
          <li>The 3-day grace period has ended</li>
          <li><strong>${vars?.expired_credits || 0} credits</strong> have expired</li>
          <li>Your subscription is now paused</li>
        </ul>
        <p style="margin-top: 15px;"><strong>How to fix:</strong> Update your payment method to resume your subscription. Your credits will be refilled at the start of your next billing cycle.</p>
      </div>
    `
  }),
  
  credit_reminder: (vars?: Record<string, any>) => {
    const threshold = vars?.threshold || '20%';
    const percentage = vars?.percentage || 0;
    const creditsRemaining = vars?.credits_remaining || 0;
    const creditsMonthly = vars?.credits_monthly || 0;
    const tier = vars?.tier || 'your plan';
    
    let urgency = 'running low';
    let emoji = '💳';
    if (threshold === '5%') {
      urgency = 'critically low';
      emoji = '🚨';
    } else if (threshold === '10%') {
      urgency = 'very low';
      emoji = '⚠️';
    }
    
    return {
      subject: `${emoji} Credit Alert: Only ${percentage}% Remaining`,
      header: `Your Credits Are ${urgency.charAt(0).toUpperCase() + urgency.slice(1)}`,
      message: `You have only **${creditsRemaining} of ${creditsMonthly} credits** remaining on your ${tier} plan (${percentage}%).\n\nTo avoid service interruption, we recommend topping up your credits now.`,
      ctaText: "Top Up Credits Now",
      ctaUrl: "https://email2contact.com/settings",
      infoboxHtml: `
        <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0;">
          <strong>💡 Tip:</strong> Consider upgrading to a higher plan if you frequently run low on credits.
        </div>
      `,
    };
  },
  
  subscription_renewal: (vars?: { 
    subscriptionTier?: string;
    creditsRefilled?: number;
    billingCycle?: string;
    nextBillingDate?: string;
  }) => ({
    subject: "✅ Subscription Renewed Successfully",
    header: "Your Subscription Has Been Renewed",
    message: `Great news! Your ${vars?.subscriptionTier || 'Email2Contact'} subscription has been successfully renewed and your credits have been refilled.\n\nThank you for continuing with Email2Contact!`,
    ctaText: "View Subscription Details",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <div style="background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0;">
        <ul style="list-style:none;padding:0;margin:0;">
          ${vars?.subscriptionTier ? `<li><strong>Plan:</strong> ${vars.subscriptionTier}</li>` : ''}
          ${vars?.creditsRefilled ? `<li><strong>Credits refilled:</strong> ${vars.creditsRefilled}</li>` : ''}
          ${vars?.billingCycle ? `<li><strong>Billing cycle:</strong> ${vars.billingCycle}</li>` : ''}
          ${vars?.nextBillingDate ? `<li><strong>Next billing date:</strong> ${vars.nextBillingDate}</li>` : ''}
        </ul>
      </div>
    `
  }),

  subscription_activated: (vars?: { tier?: string; credits?: string; billingCycle?: string }) => ({
    subject: "Subscription Activated – Email2Contact",
    header: `Welcome to Email2Contact ${vars?.tier ? vars.tier.charAt(0).toUpperCase() + vars.tier.slice(1) : ''}!`,
    message: `Your subscription is now active and your credits are ready to use. Start extracting contacts from your emails!`,
    ctaText: "Go to Settings",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <ul style="list-style:none;padding:0;margin:0;">
        ${vars?.tier ? `<li><strong>Plan:</strong> ${vars.tier.charAt(0).toUpperCase() + vars.tier.slice(1)}</li>` : ''}
        ${vars?.credits ? `<li><strong>Monthly credits:</strong> ${vars.credits}</li>` : ''}
        ${vars?.billingCycle ? `<li><strong>Billing:</strong> ${vars.billingCycle}</li>` : ''}
      </ul>
    `
  }),

  subscription_created: (vars?: { subscriptionTier?: string; billingCycle?: string; creditsIncluded?: number; nextBillingDate?: string }) => ({
    subject: `🎉 Welcome to ${vars?.subscriptionTier?.toUpperCase() || 'Email2Contact'} – Subscription Active!`,
    header: '🎉 Subscription Active!',
    message: `Your **${vars?.subscriptionTier?.toUpperCase() || 'subscription'}** is now active and ready to use!`,
    ctaText: "Go to Dashboard",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <div style="background: #e8f5e9; border-left: 4px solid #4caf50; padding: 15px; margin: 20px 0;">
        <strong>✅ Your Plan Details:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li><strong>Plan:</strong> ${vars?.subscriptionTier?.toUpperCase() || 'N/A'}</li>
          <li><strong>Billing Cycle:</strong> ${vars?.billingCycle || 'N/A'}</li>
          <li><strong>Credits Included:</strong> ${vars?.creditsIncluded || 0}</li>
          <li><strong>Next Billing Date:</strong> ${vars?.nextBillingDate || 'N/A'}</li>
        </ul>
      </div>
    `
  }),

  credits_purchased: (vars?: { credits?: string; amount?: string; package?: string }) => ({
    subject: "Credits Added – Email2Contact",
    header: "Credits Successfully Added!",
    message: `Your credit top-up has been processed. You can now continue extracting contacts seamlessly.`,
    ctaText: "Go to Settings",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <ul style="list-style:none;padding:0;margin:0;">
        ${vars?.package ? `<li><strong>Package:</strong> ${vars.package}</li>` : ''}
        ${vars?.credits ? `<li><strong>Credits added:</strong> ${vars.credits}</li>` : ''}
        ${vars?.amount ? `<li><strong>Amount:</strong> €${vars.amount}</li>` : ''}
      </ul>
    `
  }),
  
  plan_upgrade: (vars?: Record<string, any>) => {
    const currentTier = vars?.current_tier || 'starter';
    const suggestedTier = vars?.suggested_tier || 'pro';
    const percentageUsed = vars?.percentage_used || 0;
    const usageLast7Days = vars?.usage_last_7_days || 0;
    const currentCredits = vars?.current_credits || 0;
    const suggestedCredits = vars?.suggested_credits || 0;
    
    return {
      subject: `⬆️ Upgrade to ${suggestedTier.charAt(0).toUpperCase() + suggestedTier.slice(1)}? You've Used ${percentageUsed}% of Your Credits`,
      header: "Your Usage Shows You're Ready for More",
      message: `You've used **${usageLast7Days} credits in the last 7 days** (${percentageUsed}% of your monthly quota on the ${currentTier} plan).\n\nUpgrading to the **${suggestedTier} plan** gives you **${suggestedCredits} credits per month** instead of ${currentCredits}, so you never have to worry about running out.`,
      ctaText: `Upgrade to ${suggestedTier.charAt(0).toUpperCase() + suggestedTier.slice(1)}`,
      ctaUrl: "https://email2contact.com/settings",
      infoboxHtml: `
        <div style="background: #e3f2fd; border-left: 4px solid #2196f3; padding: 15px; margin: 20px 0;">
          <strong>✨ Benefits of Upgrading:</strong>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li><strong>${suggestedCredits - currentCredits} more credits</strong> per month</li>
            <li>Never worry about running out mid-month</li>
            <li>Better value per credit</li>
          </ul>
        </div>
      `,
    };
  },
  
  invoice_ready: (vars: Record<string, any>) => ({
    subject: `Your Invoice ${vars.invoice_number} is Ready`,
    header: '📄 Your Invoice is Ready',
    message: `
      Thank you for your purchase${vars.company_name ? ` with ${vars.company_name}` : ''}!
      
      Your invoice has been generated and is ready for download.
    `,
    ctaText: 'View Invoice',
    ctaUrl: vars.invoice_url,
    infoboxHtml: `
      <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <p style="margin: 0 0 10px 0;"><strong>Invoice Number:</strong> ${vars.invoice_number}</p>
        <p style="margin: 0 0 10px 0;"><strong>Amount:</strong> ${vars.amount} ${vars.currency}</p>
        ${vars.company_name ? `<p style="margin: 0;"><strong>Company:</strong> ${vars.company_name}</p>` : ''}
      </div>
      
      <p style="color: #666; font-size: 14px; margin-top: 20px;">
        💡 <strong>Tip:</strong> Save this invoice for your accounting records.
      </p>
      
      ${vars.invoice_pdf ? `
        <p style="text-align: center; margin-top: 20px;">
          <a href="${vars.invoice_pdf}" 
             style="color: #0066cc; text-decoration: underline; font-size: 14px;">
            📥 Download PDF
          </a>
        </p>
      ` : ''}
    `,
  }),
  
  team_invite: (vars: { inviter: string; inviteUrl: string }) => ({
    subject: "You've been invited to a team on Email2Contact",
    header: "Join your team on Email2Contact",
    message: `${vars.inviter} invited you to join their Email2Contact workspace. Click below to accept.`,
    ctaText: "Accept Invite",
    ctaUrl: vars.inviteUrl
  }),
  
  invoice_download: (vars: { invoiceUrl: string }) => ({
    subject: "Your invoice is ready",
    header: "Invoice available for download",
    message: "Your recent invoice is now available. Click below to download the PDF.",
    ctaText: "Download Invoice PDF",
    ctaUrl: vars.invoiceUrl
  }),
  
  security_notification: (vars: { deviceInfo: string; date: string }) => ({
    subject: "New sign-in detected",
    header: "Security alert: new login",
    message: `We noticed a new login from ${vars.deviceInfo} on ${vars.date}. If this wasn't you, please reset your password immediately.`,
    ctaText: "Reset Password",
    ctaUrl: "https://email2contact.com/reset"
  }),
  
  reactivation: () => ({
    subject: "Still using Outlook daily?",
    header: "Let AI organize again",
    message: "You haven't used Email2Contact in a while. Your AI assistant is ready to extract and organize your contacts again.",
    ctaText: "Open Outlook",
    ctaUrl: "https://email2contact.com/outlook"
  }),
  
  feedback_request: () => ({
    subject: "How's your Email2Contact experience?",
    header: "We'd love your feedback",
    message: "Please rate your experience so far:",
    ctaText: "Give Feedback",
    ctaUrl: "https://email2contact.com/feedback"
  }),
  
  subscription_cancelled: (vars?: { cancellation_date?: string; end_date?: string; credits_remaining?: string }) => ({
    subject: "Subscription Cancelled – Email2Contact",
    header: "Your Subscription Has Been Cancelled",
    message: `We're sorry to see you go! Your subscription has been cancelled and will remain active until the end of your current billing period.`,
    ctaText: "Reactivate Subscription",
    ctaUrl: "https://email2contact.com/plans",
    infoboxHtml: `
      <div style="background: #fff3cd; border-left: 4px solid #ff9800; padding: 15px; margin: 20px 0;">
        <strong>⚠️ Important Information:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          ${vars?.cancellation_date ? `<li><strong>Cancelled on:</strong> ${vars.cancellation_date}</li>` : ''}
          ${vars?.end_date ? `<li><strong>Access until:</strong> ${vars.end_date}</li>` : ''}
          ${vars?.credits_remaining ? `<li><strong>Remaining credits:</strong> ${vars.credits_remaining}</li>` : ''}
          <li><strong>⚠️ Important:</strong> All unused credits will expire on ${vars?.end_date || 'the end of your billing period'}.</li>
        </ul>
      </div>
    `
  }),

  subscription_reactivated: (vars?: { reactivation_date?: string; tier?: string; next_billing_date?: string }) => ({
    subject: "Welcome Back – Email2Contact",
    header: "Your Subscription Has Been Reactivated",
    message: `We're glad to have you back! Your subscription has been successfully reactivated.`,
    ctaText: "Go to Dashboard",
    ctaUrl: "https://email2contact.com",
    infoboxHtml: `
      <div style="background: #e8f5e9; border-left: 4px solid #4caf50; padding: 15px; margin: 20px 0;">
        <strong>✅ Reactivation Details:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li><strong>Active plan:</strong> ${vars?.tier || 'Your plan'}</li>
          <li><strong>Reactivated on:</strong> ${vars?.reactivation_date || 'Today'}</li>
          <li><strong>Next billing date:</strong> ${vars?.next_billing_date || 'End of current period'}</li>
          <li><strong>What's next:</strong> You can continue using Email2Contact without interruption. All features are now available.</li>
        </ul>
      </div>
    `
  }),

  subscription_updated: (vars?: { old_plan?: string; new_plan?: string; effective_date?: string; is_scheduled?: boolean }) => ({
    subject: `${vars?.is_scheduled ? 'Scheduled ' : ''}Plan Change Confirmation – Email2Contact`,
    header: `Your Plan ${vars?.is_scheduled ? 'Will Be' : 'Has Been'} Updated`,
    message: vars?.is_scheduled 
      ? `Your subscription plan will change from **${vars?.old_plan || 'your current plan'}** to **${vars?.new_plan || 'the new plan'}** on **${vars?.effective_date || 'your next billing date'}**.\n\nYou can modify or cancel this change anytime before the effective date in your Stripe Customer Portal.`
      : `Your subscription plan has been updated from **${vars?.old_plan || 'your previous plan'}** to **${vars?.new_plan || 'your new plan'}** effective **${vars?.effective_date || 'immediately'}**.`,
    ctaText: "Manage Subscription",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: vars?.is_scheduled 
      ? `
        <div style="background: #e3f2fd; border-left: 4px solid #2196f3; padding: 15px; margin: 20px 0;">
          <strong>📅 Scheduled Change:</strong>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li><strong>Current plan:</strong> ${vars?.old_plan || 'N/A'}</li>
            <li><strong>New plan:</strong> ${vars?.new_plan || 'N/A'}</li>
            <li><strong>Effective date:</strong> ${vars?.effective_date || 'Next billing date'}</li>
            <li><strong>Action:</strong> No action needed. Change will apply automatically.</li>
          </ul>
        </div>
      `
      : `
        <div style="background: #e8f5e9; border-left: 4px solid #4caf50; padding: 15px; margin: 20px 0;">
          <strong>✅ Plan Updated:</strong>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li><strong>Previous plan:</strong> ${vars?.old_plan || 'N/A'}</li>
            <li><strong>New plan:</strong> ${vars?.new_plan || 'N/A'}</li>
            <li><strong>Effective:</strong> ${vars?.effective_date || 'Immediately'}</li>
          </ul>
        </div>
      `
  }),

  subscription_scheduled: (vars?: { scheduled_tier?: string; scheduled_cycle?: string; current_tier?: string; current_cycle?: string; effective_date?: string }) => ({
    subject: "Plan Change Scheduled – Email2Contact",
    header: "Your Plan Change Has Been Scheduled",
    message: `Your subscription plan will automatically change on ${vars?.effective_date || 'your next billing date'}. You can modify or cancel this change anytime before the effective date.`,
    ctaText: "Manage Subscription",
    ctaUrl: "https://email2contact.com/settings",
    infoboxHtml: `
      <div style="background: #e3f2fd; border-left: 4px solid #2196f3; padding: 15px; margin: 20px 0;">
        <strong>📅 Scheduled Change:</strong>
        <ul style="margin: 10px 0; padding-left: 20px;">
          <li><strong>Current plan:</strong> ${vars?.current_tier || 'N/A'} (${vars?.current_cycle || 'N/A'})</li>
          <li><strong>New plan:</strong> ${vars?.scheduled_tier || 'N/A'} (${vars?.scheduled_cycle || 'N/A'})</li>
          <li><strong>Effective date:</strong> ${vars?.effective_date || 'Next billing date'}</li>
          <li><strong>Action:</strong> Change will apply automatically. You can modify or cancel in the Stripe Customer Portal.</li>
        </ul>
      </div>
    `
  }),
  
  gdpr_deletion: () => ({
    subject: "Your account has been deleted",
    header: "Account deleted successfully",
    message: "Your Email2Contact account and associated data have been permanently deleted in compliance with GDPR.",
    ctaText: "Learn more",
    ctaUrl: "https://email2contact.com/privacy"
  })
};
